import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { LoadingProvider } from './contexts/LoadingContext';
import theme from './assets/styles/theme';
import Footer from './components/layout/Footer';
import PublicLayout from './components/layout/PublicLayout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import MainLayout from './components/layout/MainLayout';
import EntryPass from './pages/EntryPass';
import ProtectedMemberRoute from './components/auth/ProtectedMemberRoute';
import CheckInHistory from './pages/CheckInHistory';
import Home from './pages/Home';
import FAQ from './pages/FAQ';
import ProtectedAdminRoute from './components/auth/ProtectedAdminRoute';
import Checkins from './pages/admin/Checkins';
import AdminClients from './pages/admin/AdminClients';
import ClientDetails from './pages/admin/ClientDetails';
import Guide from './pages/admin/Guide';
import Fees from './pages/Fees';
import NotFound from './pages/NotFound';
import Register from './pages/Register';
import Children from './pages/Children';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import TMSMetrics from './pages/admin/TMSMetrics';
import AdminChildren from './pages/admin/AdminChildren';
import ImportClients from './pages/admin/ImportClients';
import ImportChildren from './pages/admin/ImportChildren';
import ImportRelationship from './pages/admin/ImportRelationship';
import Todd from './pages/admin/Todd';
import SyncHistory from './pages/admin/SyncHistory';
import VerifyMagicLink from './pages/auth/VerifyMagicLink';
import EmailSettings from './pages/admin/EmailSettings';
import GoogleOAuthCallback from './pages/admin/GoogleOAuthCallback';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" />;
  }
  return children;
};

// Protected Layout Component
const ProtectedLayout = ({ children }) => (
  <ProtectedRoute>
    <MainLayout>{children}</MainLayout>
  </ProtectedRoute>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <LoadingProvider>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={
                  <PublicLayout>
                    <Home />
                  </PublicLayout>
                } />
                <Route path="/register" element={
                  <PublicLayout>
                    <Register />
                  </PublicLayout>
                } />
                <Route path="/faq" element={
                  <PublicLayout>
                    <FAQ />
                  </PublicLayout>
                } />
                <Route path="/login" element={
                  <PublicLayout>
                    <Login />
                  </PublicLayout>
                } />
                <Route path="/auth/verify" element={
                  <PublicLayout>
                    <VerifyMagicLink />
                  </PublicLayout>
                } />
                <Route path="/terms" element={
                  <PublicLayout>
                    <Terms />
                  </PublicLayout>
                } />
                <Route path="/privacy" element={
                  <PublicLayout>
                    <Privacy />
                  </PublicLayout>
                } />

                {/* Protected Routes */}
                <Route path="/dashboard" element={
                  <ProtectedLayout>
                    <Dashboard />
                  </ProtectedLayout>
                } />
                <Route path="/profile" element={
                  <ProtectedLayout>
                    <Profile />
                  </ProtectedLayout>
                } />
                <Route path="/entry-pass" element={
                  <ProtectedLayout>
                    <ProtectedMemberRoute>
                      <EntryPass />
                    </ProtectedMemberRoute>
                  </ProtectedLayout>
                } />
                <Route path="/check-in-history" element={
                  <ProtectedLayout>
                    <CheckInHistory />
                  </ProtectedLayout>
                } />
                <Route path="/fees" element={
                  <ProtectedLayout>
                    <Fees />
                  </ProtectedLayout>
                } />
                <Route path="/children" element={
                  <ProtectedLayout>
                    <Children />
                  </ProtectedLayout>
                } />

                {/* Admin Routes */}
                <Route path="/admin/checkins" element={
                  <ProtectedLayout>
                    <ProtectedAdminRoute>
                      <Checkins />
                    </ProtectedAdminRoute>
                  </ProtectedLayout>
                } />
                <Route path="/admin/clients" element={
                  <ProtectedLayout>
                    <ProtectedAdminRoute>
                      <AdminClients />
                    </ProtectedAdminRoute>
                  </ProtectedLayout>
                } />
                <Route path="/admin/clients/:id" element={
                  <ProtectedLayout>
                    <ProtectedAdminRoute>
                      <ClientDetails />
                    </ProtectedAdminRoute>
                  </ProtectedLayout>
                } />
                <Route path="/admin/guide" element={
                  <ProtectedLayout>
                    <ProtectedAdminRoute>
                      <Guide />
                    </ProtectedAdminRoute>
                  </ProtectedLayout>
                } />
                <Route path="/admin/metrics" element={
                  <ProtectedLayout>
                    <ProtectedAdminRoute>
                      <TMSMetrics />
                    </ProtectedAdminRoute>
                  </ProtectedLayout>
                } />
                <Route path="/admin/children" element={
                  <ProtectedLayout>
                    <ProtectedAdminRoute>
                      <AdminChildren />
                    </ProtectedAdminRoute>
                  </ProtectedLayout>
                } />
                <Route path="/admin/import-clients" element={
                  <ProtectedLayout>
                    <ProtectedAdminRoute>
                      <ImportClients />
                    </ProtectedAdminRoute>
                  </ProtectedLayout>
                } />
                <Route path="/admin/import-children" element={
                  <ProtectedLayout>
                    <ProtectedAdminRoute>
                      <ImportChildren />
                    </ProtectedAdminRoute>
                  </ProtectedLayout>
                } />
                <Route path="/admin/import-relationships" element={
                  <ProtectedLayout>
                    <ProtectedAdminRoute>
                      <ImportRelationship />
                    </ProtectedAdminRoute>
                  </ProtectedLayout>
                } />
                <Route path="/admin/sync-history" element={
                  <ProtectedLayout>
                    <ProtectedAdminRoute>
                      <SyncHistory />
                    </ProtectedAdminRoute>
                  </ProtectedLayout>
                } />
                <Route path="/todd" element={
                  <ProtectedLayout>
                    <ProtectedAdminRoute>
                      <Todd />
                    </ProtectedAdminRoute>
                  </ProtectedLayout>
                } />
                <Route path="/admin/email-settings" element={
                  <ProtectedLayout>
                    <ProtectedAdminRoute>
                      <EmailSettings />
                    </ProtectedAdminRoute>
                  </ProtectedLayout>
                } />
                <Route path="/admin/oauth2callback" element={
                  <PublicLayout>
                    <GoogleOAuthCallback />
                  </PublicLayout>
                } />

                {/* 404 Route - Must be last */}
                <Route path="*" element={
                  <PublicLayout>
                    <NotFound />
                  </PublicLayout>
                } />
              </Routes>
            </Box>
          </LoadingProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
